import React, { ComponentType, FC, ReactNode, useEffect, useRef, useState } from 'react'
import Head from 'next/head'
import polaroidStyles from './Polaroid.module.scss'
import {
  ARTWORK_STATUS,
  getArtworkInfo,
  getCleanCategory,
  getImageAltText,
  getImageSrc,
  getPrintAltText,
  isCategorySculpture,
} from './helpers'
import { SAArtwork } from 'types/Artwork'
import PolaroidActions from './Actions/PolaroidActions'
import classNames from 'classnames'
import { convertFromCentsToDollars } from 'components/FormatPrice/helpers'
import { LocaleCurrency } from 'components/FormatPrice'
import SALink from 'components/SALink'
import useMasonry from './useMasonry'
import { usePolaroidEvents } from './usePolaroidEvents'
import PriceStatusRow from './components/PriceStatusRow'
import { PolaroidSkeleton } from './components/PolaroidSkeleton'
import { addToCartGAWhitelist } from './Actions/useAddToCart'
import { capitalizeString } from 'lib/helpers'
import useQA from 'hooks/useQA'
import useSearchProvider from 'components/SA/SearchProvider/hooks/useSearchProvider'
import PolaroidText from './components/PolaroidText'
import MediumMaterialDimensionsRow from './components/templates/MediumMaterialDimensionsRow'
import PolaroidArtistName from './components/templates/PolaroidArtistName'
import useIntersectionObserver from 'hooks/intersectionObserver'
import DataLayer, { Impression } from 'lib/dataLayer'
import AlgoliaEvents from 'lib/algoliaEvents'
import { getAlgoliaQueryIDFromLocalStorage } from 'lib/algolia/v2/helpers'
import { useSelector } from 'react-redux'
import { getIsMobileTraffic } from 'selectors/shared/mobileTraffic'
import PolaroidArtworkTitle from './components/templates/PolaroidArtworkTitle'
import PolaroidPrintsQuantity from './components/templates/PolaroidPrintsQuantity'

export type PolaroidSpecificPageProps = {
  algoliaIndice?: string
  algoliaUserToken?: string
  dataApi?: string
  dataList?:
    | 'plp-results'
    | 'feature-results'
    | 'favorites-results'
    | 'artist-profile-all-artworks'
    | 'curated-deals-artworks'
    | 'show-page'
    | 'collection-detail'
    | 'aa-selection'
    | 'curation-portal-results'
  dataListId?: string
  dataListName?: string
}

export type CustomPolaroidInfoProps = PolaroidSpecificPageProps & {
  artwork: SAArtwork
  userID?: number
  isPrintForDataLayerEvent?: boolean
  shouldSendAlgoliaEvents?: boolean
  customPromoCode?: string
  gaListId?: string
}

type PolaroidProps = PolaroidSpecificPageProps & {
  artwork: SAArtwork
  priority?: boolean
  lazyLoad?: boolean
  isImageSrcFromProd?: boolean
  customStyle?: string
  isArtistNameVisible?: boolean
  isPrintsFromVisible?: boolean
  userID?: number
  isUsersArtworksAccount?: boolean
  isMediumMaterialVisible?: boolean
  isDimensionsVisible?: boolean
  showPrintsFromInPrice?: boolean
  showOriginalPrice?: boolean
  customActions?: ReactNode
  customPromoCode?: string
  hasPrints?: boolean
  isPrintForDataLayerEvent?: boolean
  imageSuffix?: string
  shouldSendAlgoliaEvents?: boolean
  CustomPolaroidHeader?: ComponentType<CustomPolaroidInfoProps>
  CustomPolaroidInfo?: ComponentType<CustomPolaroidInfoProps>
  showPrintsQuantity?: boolean
  shouldIncludeSearchProviderAttributes?: boolean
  customArtworkClick?: (ev: React.SyntheticEvent<Record<string, any>>) => void
  polaroidInfoSpanCount?: number // This is a way to fix the masonry if we are using custom components to render the polaroid info
}

const Polaroid: FC<PolaroidProps> = ({
  artwork,
  priority,
  lazyLoad,
  isImageSrcFromProd,
  algoliaIndice,
  algoliaUserToken,
  dataApi = '',
  dataList,
  dataListId = '',
  dataListName = '',
  customStyle = '',
  isArtistNameVisible = true,
  isPrintsFromVisible = true,
  userID = 0,
  isUsersArtworksAccount,
  isMediumMaterialVisible = true,
  isDimensionsVisible = true,
  showPrintsFromInPrice,
  showOriginalPrice,
  customActions,
  customPromoCode,
  hasPrints,
  isPrintForDataLayerEvent,
  imageSuffix = 'pdp',
  shouldSendAlgoliaEvents = true,
  CustomPolaroidHeader,
  CustomPolaroidInfo,
  showPrintsQuantity,
  shouldIncludeSearchProviderAttributes = true,
  customArtworkClick,
  polaroidInfoSpanCount,
}) => {
  const { testId } = useQA()
  const { getSearchProviderAttributes } = useSearchProvider()
  const {
    artworkId,
    artworkTitle,
    aspectRatio,
    category,
    positionID,
    depth,
    hasPrint,
    hasOpenEditions,
    height,
    isReadyToHang: isReadyToHangNumber,
    listPrice,
    materials,
    mediums,
    minPrintPrice,
    profileUrl,
    originalStatus,
    sku,
    styles,
    subject,
    width,
    isSponsored,
    slCampaignId,
    slCampaignOwner,
  } = artwork
  const capitalizedArtworkTitle = capitalizeString(artworkTitle)
  const isSculpture = isCategorySculpture(category)
  const cleanCategory = getCleanCategory(category)
  const capitalizedCleanCategory = capitalizeString(cleanCategory)

  const [imageDimensions, setImageDimensions] = useState<{
    width: number | undefined
    height: number | undefined
  }>({
    width: 200, // just a default value to core web vitals
    height: parseInt((200 / aspectRatio).toString(), 10), // just a default value to core web vitals
  })
  const figureRef = useRef<HTMLElement | null>(null)
  const [isSkeletonVisible, setIsSkeletonVisible] = useState(true)
  const imageRef = useRef<HTMLImageElement | null>(null)

  const { hasViewed } = useIntersectionObserver(imageRef)
  const isMobileTraffic = useSelector(getIsMobileTraffic)
  const algoliaQueryIdFromLocalStorage = getAlgoliaQueryIDFromLocalStorage()

  const isAvailable = originalStatus === ARTWORK_STATUS.AVAILABLE
  const isSold = originalStatus === ARTWORK_STATUS.SOLD
  const isUnavailable = originalStatus === ARTWORK_STATUS.UNAVAILABLE
  const isReserved = originalStatus === ARTWORK_STATUS.RESERVED

  const {
    artistName,
    country,
    dataPosition,
    href,
    src,
    printDimensions,
    printMaterials,
    printFrames,
  } = getArtworkInfo({
    artwork,
    hasPrints: hasPrints || (isSold && hasOpenEditions) || (isUnavailable && hasOpenEditions),
  })
  const imageSrc = getImageSrc({
    originalSrc: src,
    priority: isImageSrcFromProd,
    imageSuffix,
  })
  const capitalizedArtistName = capitalizeString(artistName)
  const capitalizedCountry = capitalizeString(country)

  const getFigureDimensions = () => {
    if (figureRef.current) {
      const { width } = figureRef.current.getBoundingClientRect()
      const height = parseInt((width / aspectRatio).toString(), 10)
      const roundedWidth = parseInt(width.toString(), 10)
      setImageDimensions({ width: roundedWidth, height })
    }
  }

  const handleLoadImage = () => {
    setIsSkeletonVisible(false)
    getFigureDimensions()
  }

  // Trigger all Google Analytics events
  const { handleArtworkClick } = usePolaroidEvents({
    artworkId,
    artworkTitle: capitalizedArtworkTitle,
    artistName: capitalizedArtistName,
    positionID,
    category,
    listPrice,
    sku,
    algoliaIndice,
    algoliaUserToken,
    dataList,
    dataListId,
    dataListName,
    userID,
    isPrintForDataLayerEvent,
    shouldSendAlgoliaEvents,
  })

  const handleCustomArtworkClick = (ev) => {
    if (Boolean(customArtworkClick)) {
      customArtworkClick?.(ev)
      return
    }

    handleArtworkClick(ev)
  }

  const altParams = {
    artistName: capitalizedArtistName,
    subject,
    styles,
    category,
  }
  const productEvent = addToCartGAWhitelist({
    artistName: capitalizedArtistName,
    category,
    listId: dataList,
    price: listPrice,
    sku,
    title: capitalizedArtworkTitle,
    dataPosition,
    artworkId,
  })

  const isReadyToHang = isReadyToHangNumber === 1
  const shouldOpenNewDesktopPdpTab = !isMobileTraffic
  const shouldMediumMaterialAndDimensionsRender =
    !((isSold || isUnavailable) && hasOpenEditions) &&
    (isMediumMaterialVisible || isDimensionsVisible)
  const shouldPrintsFromRender =
    isPrintsFromVisible && minPrintPrice > 0 && ((isAvailable && hasOpenEditions) || isReserved)
  const shoulReadyToHangRender = isReadyToHang && (isAvailable || isReserved)

  // number of elements in Polaroid to calculate masonry
  const infoSpanConditions = [
    true, // image
    true, // price or status
    true, // artwork title
    isArtistNameVisible, // artist name
    shouldMediumMaterialAndDimensionsRender, // medium/material and dimensions
    showOriginalPrice, // Original Price
    shouldPrintsFromRender, // Prints From
    shoulReadyToHangRender, // ready to hang
  ]
  const infoSpanCount = infoSpanConditions.filter((value) => value === true).length
  const { totalSpanCount } = useMasonry({
    imageDimensions,
    aspectRatio,
    infoSpanCount: polaroidInfoSpanCount || infoSpanCount,
  })

  const baseConstructorTrackingAttributes = [
    { 'item-id': artworkId },
    { 'item-name': capitalizedArtworkTitle },
    { 'item-price': listPrice },
  ]
  const sponsoredTrackingAttributes = [
    { 'sl-campaign-id': slCampaignId || '' },
    { 'sl-campaign-owner': slCampaignOwner || '' },
  ]
  const constructorTrackingAttributes = isSponsored
    ? [...baseConstructorTrackingAttributes, ...sponsoredTrackingAttributes]
    : baseConstructorTrackingAttributes

  // Send data layer events of viewed polaroid by user
  useEffect(() => {
    if (hasViewed) {
      // This is the new dataLayer event for GA4
      const impression: Impression = {
        // Strip trailing `s` - Paintings => Painting
        // Change Mixed Media => Mixedmedia
        item_category: category?.replace(/s$/, '').replace(/\sM/, 'm'),
        item_id: sku || '',
        item_art_id: artworkId || '',
        item_name: artworkTitle || '',
        item_brand: artistName || '',
        item_variant: isPrintForDataLayerEvent ? 'open' : 'original',
        index: dataPosition,
        affiliation: 'Online Store',
        price: convertFromCentsToDollars(listPrice) || 0,
        quantity: 1,
        original_availability: originalStatus,
      }
      DataLayer.addImpressionEvent(impression, dataListId, capitalizeString(dataListName))

      if (shouldSendAlgoliaEvents) {
        AlgoliaEvents.addImpressionEvent(
          artworkId,
          algoliaIndice,
          algoliaUserToken,
          algoliaQueryIdFromLocalStorage,
          userID
        )
      }
    }
  }, [hasViewed])
  /* =============================================================================================== */

  useEffect(() => {
    if (imageRef.current?.complete) {
      setIsSkeletonVisible(false)
    }

    getFigureDimensions()
    window.addEventListener('resize', getFigureDimensions)

    // To calculate width and heigh of images also if an element in the page changes the width of the <figure> (because now figure is 1fr using grid system)
    const resizeObserver = new ResizeObserver(() => {
      getFigureDimensions()
    })

    if (figureRef.current) {
      resizeObserver.observe(figureRef.current)
    }

    return () => {
      window.removeEventListener('resize', getFigureDimensions)
      if (figureRef.current) {
        resizeObserver.unobserve(figureRef.current)
      }
    }
  }, [])

  return (
    <>
      {priority && (
        /* Preload Images */
        <Head>
          <link
            key={`__nimg-${imageSrc}`}
            rel='preload'
            fetchPriority='high'
            as='image'
            href={imageSrc}
          />
        </Head>
      )}
      <figure
        className={classNames(polaroidStyles.polaroidContainer, {
          [customStyle]: customStyle !== '',
        })}
        style={{
          gridRowEnd: `span ${totalSpanCount}`,
        }}
        ref={figureRef}
        {...(shouldIncludeSearchProviderAttributes
          ? getSearchProviderAttributes(constructorTrackingAttributes)
          : {})}
      >
        {!!CustomPolaroidHeader && (
          <CustomPolaroidHeader
            artwork={artwork}
            algoliaIndice={algoliaIndice}
            algoliaUserToken={algoliaUserToken}
            dataApi={dataApi}
            dataList={dataList}
            dataListId={dataListId}
            dataListName={dataListName}
            userID={userID}
            isPrintForDataLayerEvent={isPrintForDataLayerEvent}
            shouldSendAlgoliaEvents={shouldSendAlgoliaEvents}
          />
        )}

        <SALink
          data-test-item='artwork-image-link'
          aria-label={capitalizedArtworkTitle}
          data-api={dataApi}
          data-id={sku}
          data-name={capitalizedArtworkTitle}
          data-position={dataPosition}
          data-list={dataList}
          href={href}
          onClick={handleCustomArtworkClick}
          rel={capitalizedArtworkTitle}
          target={shouldOpenNewDesktopPdpTab ? '_blank' : ''}
          title={`${capitalizedArtworkTitle} ${capitalizedCleanCategory}`}
          className={polaroidStyles.figureImageContainer}
          style={{
            paddingTop: `calc(calc(100% / ${aspectRatio}) + 8px)`, // 8px of spacing between image and text as it is an absolute position
          }}
        >
          <PolaroidSkeleton
            width={imageDimensions.width}
            height={imageDimensions.height}
            customClassName={classNames(polaroidStyles.skeletonVisible, {
              [polaroidStyles.skeletonInvisible]: !isSkeletonVisible,
            })}
          />
          <img
            {...(lazyLoad
              ? {
                  src: imageSrc,
                  loading: 'lazy',
                }
              : {
                  src: imageSrc,
                })}
            onLoad={handleLoadImage}
            className={classNames({
              [polaroidStyles.figureVisible]: !isSkeletonVisible,
              [polaroidStyles.figureInvisible]: isSkeletonVisible,
            })}
            ref={imageRef}
            alt={hasPrint ? getPrintAltText(altParams) : getImageAltText(altParams)}
            width={imageDimensions.width}
            height={imageDimensions.height}
          />
        </SALink>
        {!!CustomPolaroidInfo ? (
          <CustomPolaroidInfo
            artwork={artwork}
            algoliaIndice={algoliaIndice}
            algoliaUserToken={algoliaUserToken}
            dataApi={dataApi}
            dataList={dataList}
            dataListId={dataListId}
            dataListName={dataListName}
            userID={userID}
            customPromoCode={customPromoCode}
            isPrintForDataLayerEvent={isPrintForDataLayerEvent}
            shouldSendAlgoliaEvents={shouldSendAlgoliaEvents}
          />
        ) : (
          <>
            <div className={polaroidStyles.polaroidPriceAndActionsContainer}>
              <PriceStatusRow
                isSold={isSold}
                isReserved={isReserved}
                isUnavailable={isUnavailable}
                hasOpenEditions={hasOpenEditions}
                minPrintPrice={minPrintPrice}
                listPrice={listPrice}
                showPrintsFromInPrice={showPrintsFromInPrice}
              />
              {customActions ? (
                customActions
              ) : (
                <PolaroidActions
                  artwork={artwork}
                  artworkId={artworkId}
                  sku={sku}
                  status={originalStatus}
                  price={listPrice}
                  productEvent={productEvent}
                  shouldShowEditIcon={isUsersArtworksAccount}
                  customPromoCode={customPromoCode}
                  isAddToCartVisible={!isSold && !hasPrints}
                  shouldSendAlgoliaEvents={shouldSendAlgoliaEvents}
                  hasPrints={hasPrints}
                />
              )}
            </div>

            <div
              className={polaroidStyles.polaroidInfoContainer}
              {...testId('polaroid-info-container')}
            >
              <PolaroidArtworkTitle
                onClick={handleCustomArtworkClick}
                artworkTitleLink={href}
                artworkTitle={`${capitalizedArtworkTitle} ${capitalizedCleanCategory}`}
                shouldOpenNewDesktopPdpTab={shouldOpenNewDesktopPdpTab}
              />

              {isArtistNameVisible && (
                <PolaroidArtistName
                  artistLink={profileUrl}
                  artistFullName={capitalizedArtistName}
                  artistCountry={capitalizedCountry}
                />
              )}

              {shouldMediumMaterialAndDimensionsRender && (
                <MediumMaterialDimensionsRow
                  isMediumMaterialVisible={isMediumMaterialVisible}
                  isDimensionsVisible={isDimensionsVisible}
                  mediums={mediums}
                  materials={materials}
                  dimensions={{
                    widthInCentimeters: width,
                    heightInCentimeters: height,
                    depthInCentimeters: depth,
                  }}
                  isDepthVisible={isSculpture}
                  category={category}
                />
              )}

              {showOriginalPrice && (
                <PolaroidText dataTestId='original-price'>
                  Original <LocaleCurrency price={convertFromCentsToDollars(listPrice)} />
                </PolaroidText>
              )}

              {(showPrintsQuantity ||
                (isSold && hasOpenEditions) ||
                (isUnavailable && hasOpenEditions)) && (
                <PolaroidPrintsQuantity
                  printDimensions={printDimensions}
                  printMaterials={printMaterials}
                  printFrames={printFrames}
                />
              )}

              {shouldPrintsFromRender && (
                <PolaroidText dataTestId='prints-price'>
                  Prints From <LocaleCurrency price={convertFromCentsToDollars(minPrintPrice)} />
                </PolaroidText>
              )}

              {shoulReadyToHangRender && (
                <PolaroidText dataTestId='ready-to-hang'>Ready to hang</PolaroidText>
              )}
            </div>
          </>
        )}
      </figure>
    </>
  )
}

export default Polaroid
